import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import ScrollRestoration from './ScrollRestoration';
import { billingAuthHeaders } from '../utils/billingApi';
import {
  authAudienceConfigured,
  clearPortalScopeRecoveryState,
  clearFreshAuthRedirect,
  describePortalAccessFailure,
  forceReauthentication,
  isFreshAuthRedirect,
  hasAttemptedPortalScopeLoginRecovery,
  hasAttemptedPortalScopeRecovery,
  hasAttemptedPortalSilentAuthRecovery,
  isRecoverableAuthError,
  isReauthenticationInProgress,
  markPortalScopeLoginRecoveryAttempted,
  markPortalScopeRecoveryAttempted,
  markPortalSilentAuthRecoveryAttempted,
  portalApiTokenOptions,
  portalScopesGrantedInToken,
  requestPortalScopeAuthorization,
  resetReauthenticationState,
} from '../utils/authSession';
import { getContractApiUrl, isPortalApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getContractApiUrl();

function apiErrorDetail(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : '';
}

function isMissingPortalScopeError(error) {
  const detail = apiErrorDetail(error);
  return (
    error?.response?.status === 403
    && (detail.includes('read:contract-state') || detail.includes('write:contract-state'))
  );
}

function authErrorCode(error) {
  return String(error?.error || error?.code || '').toLowerCase();
}

async function attachPortalAccessToken(getAccessTokenSilently, { cacheMode } = {}) {
  const token = await getAccessTokenSilently(portalApiTokenOptions({ cacheMode }));
  if (typeof token !== 'string' || !token) {
    throw new Error('Portal API access token unavailable');
  }
  return token;
}

async function resolvePortalSession({
  getAccessTokenSilently,
}) {
  // Do not start a new Auth0 login from session validate. After Google SSO,
  // Auth0 returns to the SPA; a remount (React StrictMode) or missing API
  // scopes used to restart Universal Login immediately.
  const freshAuthRedirect = isFreshAuthRedirect();
  let cachedErr;
  let cachedTokenWithoutScopes;
  let token;

  // Hard refresh must reuse the cached access token when it is still valid.
  // cacheMode: 'off' always forces a refresh-token exchange and fails with
  // missing_refresh_token when offline_access was never granted.
  try {
    token = await attachPortalAccessToken(getAccessTokenSilently, {});
    if (portalScopesGrantedInToken(token)) {
      clearPortalScopeRecoveryState();
      clearFreshAuthRedirect();
      resetReauthenticationState();
      return { ok: true, token };
    }
    cachedTokenWithoutScopes = token;
  } catch (err) {
    cachedErr = err;
  }

  // Cached token unavailable or missing portal scopes — bypass cache so Auth0
  // mints a fresh API token (Post-Login Action scopes only appear on login).
  try {
    token = await attachPortalAccessToken(getAccessTokenSilently, { cacheMode: 'off' });
  } catch (refreshErr) {
    if (cachedTokenWithoutScopes) {
      return {
        ok: false,
        blocked: true,
        token: cachedTokenWithoutScopes,
        reason: describePortalAccessFailure({ token: cachedTokenWithoutScopes }),
      };
    }
    if (authErrorCode(refreshErr) === 'missing_refresh_token') {
      return {
        ok: false,
        needsSilentAuth: true,
        reason: describePortalAccessFailure({ error: refreshErr || cachedErr }),
      };
    }
    if (isRecoverableAuthError(refreshErr || cachedErr)) {
      return {
        ok: false,
        needsSilentAuth: true,
        reason: describePortalAccessFailure({ error: refreshErr || cachedErr }),
      };
    }
    console.warn('Auth session: portal token unavailable during validate', refreshErr);
    return {
      ok: false,
      blocked: true,
      reason: describePortalAccessFailure({ error: refreshErr || cachedErr }),
    };
  }

  if (portalScopesGrantedInToken(token)) {
    clearPortalScopeRecoveryState();
    clearFreshAuthRedirect();
    resetReauthenticationState();
    return { ok: true, token };
  }

  if (freshAuthRedirect) {
    console.warn('Auth session: OAuth callback succeeded without portal API scopes');
  }
  return {
    ok: false,
    blocked: true,
    token,
    reason: describePortalAccessFailure({ token }),
  };
}

async function recoverPortalSession({
  result,
  loginWithRedirect,
}) {
  const silentAlreadyAttempted = hasAttemptedPortalSilentAuthRecovery();
  if (result.needsSilentAuth && !silentAlreadyAttempted) {
    markPortalSilentAuthRecoveryAttempted();
    await requestPortalScopeAuthorization({
      loginWithRedirect,
      prompt: 'none',
    });
    return true;
  }
  const needsInteractiveRecovery = result.blocked
    || (result.needsSilentAuth && silentAlreadyAttempted);
  if (needsInteractiveRecovery && !hasAttemptedPortalScopeRecovery()) {
    markPortalScopeRecoveryAttempted();
    await requestPortalScopeAuthorization({
      loginWithRedirect,
      prompt: 'consent',
    });
    return true;
  }
  if (needsInteractiveRecovery && !hasAttemptedPortalScopeLoginRecovery()) {
    markPortalScopeLoginRecoveryAttempted();
    await requestPortalScopeAuthorization({
      loginWithRedirect,
      prompt: 'login',
    });
    return true;
  }
  return false;
}

/**
 * On reload, verify the Auth0 session can still mint API tokens.
 * When it cannot, force logout + login instead of showing per-page auth errors.
 */
const AuthSessionGuard = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
    loginWithRedirect,
    user,
  } = useAuth0();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [portalScopeBlocked, setPortalScopeBlocked] = useState(false);
  const [portalBlockReason, setPortalBlockReason] = useState('');

  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use(async (config) => {
      const existingAuth =
        config.headers?.Authorization
        || (typeof config.headers?.get === 'function'
          ? config.headers.get('Authorization')
          : undefined);
      if (
        !isAuthenticated ||
        !authAudienceConfigured() ||
        !sessionChecked ||
        !isPortalApiUrl(config.url) ||
        existingAuth ||
        portalScopeBlocked ||
        isReauthenticationInProgress()
      ) {
        return config;
      }
      try {
        const token = await attachPortalAccessToken(getAccessTokenSilently);
        if (typeof config.headers?.set === 'function') {
          config.headers.set('Authorization', `Bearer ${token}`);
        } else {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      } catch (err) {
        console.warn('Auth session: could not attach API token', err);
      }
      if (!isReauthenticationInProgress()) {
        await forceReauthentication({ logout, loginWithRedirect });
      }
      return Promise.reject(new Error('Sign in required for portal API'));
    });

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const hadBearer = Boolean(
          error.config?.headers?.Authorization
          || (typeof error.config?.headers?.get === 'function'
            && error.config.headers.get('Authorization')),
        );
        const billingRoute = (error.config?.url || '').includes('/billing/');
        const scopeRejected = isMissingPortalScopeError(error);
        const authRejected = error.response?.status === 401
          || scopeRejected
          || (error.response?.status === 403 && billingRoute);
        if (
          authRejected &&
          isAuthenticated &&
          sessionChecked &&
          (hadBearer || authAudienceConfigured()) &&
          !portalScopeBlocked &&
          !isReauthenticationInProgress()
        ) {
          if (scopeRejected) {
            const recovered = await recoverPortalSession({
              result: { blocked: true },
              loginWithRedirect,
            });
            if (!recovered) {
              setPortalScopeBlocked(true);
            }
          } else {
            await forceReauthentication({ logout, loginWithRedirect, prompt: 'login' });
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(interceptorId);
    };
  }, [isAuthenticated, sessionChecked, getAccessTokenSilently, logout, loginWithRedirect, portalScopeBlocked]);

  useEffect(() => {
    if (!isAuthenticated || !authAudienceConfigured()) {
      return undefined;
    }
    const nativeFetch = window.fetch;
    window.fetch = async (input, init) => {
      try {
        let url = '';
        if (typeof input === 'string') {
          url = input;
        } else if (typeof URL !== 'undefined' && input instanceof URL) {
          url = input.toString();
        } else if (input && typeof input.url === 'string') {
          url = input.url;
        }
        if (
          isPortalApiUrl(url)
          && sessionChecked
          && !portalScopeBlocked
          && !isReauthenticationInProgress()
        ) {
          const headers = new Headers(
            (init && init.headers) ||
              (typeof Request !== 'undefined' && input instanceof Request
                ? input.headers
                : undefined),
          );
          if (!headers.has('Authorization')) {
            try {
              const token = await attachPortalAccessToken(getAccessTokenSilently);
              headers.set('Authorization', `Bearer ${token}`);
              return nativeFetch(input, { ...(init || {}), headers });
            } catch (err) {
              console.warn('Auth session: could not attach API token to fetch', err);
            }
            if (!isReauthenticationInProgress()) {
              await forceReauthentication({ logout, loginWithRedirect });
            }
            throw new Error('Sign in required for portal API');
          }
        }
      } catch (err) {
        if (err?.message === 'Sign in required for portal API') {
          throw err;
        }
        console.warn('Auth session: could not attach API token to fetch', err);
      }
      return nativeFetch(input, init);
    };
    return () => {
      window.fetch = nativeFetch;
    };
  }, [isAuthenticated, sessionChecked, getAccessTokenSilently, logout, loginWithRedirect, portalScopeBlocked]);

  useEffect(() => {
    if (isLoading) {
      return undefined;
    }

    if (!isAuthenticated) {
      setSessionChecked(true);
      setPortalScopeBlocked(false);
      setPortalBlockReason('');
      return undefined;
    }

    setSessionChecked(false);

    let cancelled = false;

    const validateSession = async () => {
      try {
        if (authAudienceConfigured()) {
          const result = await resolvePortalSession({
            getAccessTokenSilently,
          });
          if (cancelled) {
            return;
          }
          if (result.ok) {
            setPortalScopeBlocked(false);
            setPortalBlockReason('');
            setSessionChecked(true);
            return;
          }
          if (result.needsSilentAuth || result.blocked) {
            const recovered = await recoverPortalSession({
              result,
              loginWithRedirect,
            });
            if (cancelled) {
              return;
            }
            if (recovered) {
              return;
            }
            clearFreshAuthRedirect();
            setPortalScopeBlocked(true);
            setPortalBlockReason(result.reason || '');
            setSessionChecked(true);
            return;
          }
          return;
        }

        await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
        resetReauthenticationState();
        if (!cancelled) {
          setSessionChecked(true);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        if (isReauthenticationInProgress()) {
          return;
        }
        console.warn('Auth session: validate failed', err);
        setPortalScopeBlocked(true);
        setPortalBlockReason(describePortalAccessFailure({ error: err }));
        setSessionChecked(true);
      }
    };

    validateSession();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
    loginWithRedirect,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !sessionChecked || !user?.email || portalScopeBlocked) {
      return;
    }
    if (sessionStorage.getItem('keyflo_login_event_fired')) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (authAudienceConfigured()) {
          const token = await attachPortalAccessToken(getAccessTokenSilently);
          headers.Authorization = `Bearer ${token}`;
        }
        const response = await fetch(`${BACKEND_URL}/auth/login-event`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            email: user.email,
            name: user.name || user.nickname || '',
            auth0_sub: user.sub || '',
            success: true,
          }),
        });
        if (!cancelled && response.ok) {
          sessionStorage.setItem('keyflo_login_event_fired', 'true');
        }
      } catch (e) { /* non-critical */ }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, sessionChecked, user, getAccessTokenSilently, portalScopeBlocked]);

  if (portalScopeBlocked) {
    return (
      <div style={{ maxWidth: 520, margin: '4rem auto', padding: '0 1.5rem', lineHeight: 1.5 }}>
        <h2>Could not restore your portal session</h2>
        <p>
          You are signed in, but KeyFlo could not obtain a portal API token with
          <code> read:contract-state</code>. This usually means Auth0 RBAC or API authorization
          needs to be updated for your account.
        </p>
        {portalBlockReason ? (
          <p><code>{portalBlockReason}</code></p>
        ) : null}
        <button
          type="button"
          onClick={async () => {
            clearPortalScopeRecoveryState();
            await requestPortalScopeAuthorization({
              loginWithRedirect,
              prompt: 'consent',
            });
          }}
        >
          Authorize portal access
        </button>
        <button
          type="button"
          onClick={() => {
            clearPortalScopeRecoveryState();
            logout({ logoutParams: { returnTo: window.location.origin } });
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  if (isLoading || (isAuthenticated && !sessionChecked)) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <ScrollRestoration />
      {children}
    </>
  );
};

export default AuthSessionGuard;
